import { FilterRange } from '@percent/lemonade'

import { useInsightsContext } from '../../context/InsightsContext'
import {
  filterFieldKeys,
  FilterDropdownAlign,
  FilterValues,
  ResolvedFilter,
} from '../../types/insights.types'
import { formatRangeBound } from '../../utils/formatValue'

/** Host-localised chrome for the number-range control (defaults are neutral English). */
export type NumberRangeFilterLabels = {
  min?: string
  max?: string
  apply?: string
  clear?: string
  /** Shown while min exceeds max or a bound is negative. */
  invalidRange?: string
}

export type NumberRangeFilterProps = {
  def: ResolvedFilter
  values: FilterValues
  /** Emits the `<key>Min` / `<key>Max` values together; `undefined` clears a bound. */
  onChange: (next: FilterValues) => void
  labels?: NumberRangeFilterLabels
  dropdownAlign?: FilterDropdownAlign
  testId: string
}

const toBound = (value: FilterValues[string]) =>
  typeof value === 'string' ? Number(value) : undefined

/**
 * The `numberRange` filter control: min / max inputs applied together, backed by
 * the `<key>Min` / `<key>Max` params. Money bounds are in major units.
 *
 * @internal Rendered by `InsightsFilters`; exported for its tests, not for hosts.
 */
export function NumberRangeFilter({
  def,
  values,
  onChange,
  labels,
  dropdownAlign = 'end',
  testId,
}: Readonly<NumberRangeFilterProps>) {
  const { locale } = useInsightsContext()
  const [minKey, maxKey] = filterFieldKeys(def)

  return (
    <FilterRange
      label={def.label}
      value={{ min: toBound(values[minKey]), max: toBound(values[maxKey]) }}
      onChange={({ min, max }) =>
        onChange({ [minKey]: min?.toString(), [maxKey]: max?.toString() })
      }
      minFieldLabel={labels?.min ?? 'Min'}
      maxFieldLabel={labels?.max ?? 'Max'}
      minPlaceholder={def.bounds?.min?.toString()}
      maxPlaceholder={def.bounds?.max?.toString()}
      invalidRangeText={
        labels?.invalidRange ??
        'Enter non-negative values, with Min no greater than Max.'
      }
      formatValue={(value) => formatRangeBound(value, def.currency, locale)}
      applyButtonText={labels?.apply ?? 'Apply'}
      clearButtonText={labels?.clear ?? 'Clear'}
      placement={`bottom-${dropdownAlign}`}
      testId={testId}
    />
  )
}
